/**
  * Loop through a list of targets element and when element enters or leaves viewPort
  * assign class names and fire event callbacks, if defined.
**/

const ENTERED_CLASS   = 'scroll-entered',
      LEFT_CLASS      = 'scroll-left',
      IN_VIEW_CLASS   = 'scroll-in-view',
      TRIGGERED_CLASS = 'scroll-triggered';

function triggerScrollTargets(targetConfigs=[], direction){
  for(let configs of targetConfigs){
    for(let el of configs.els){
      const PREV_STATE = getPreviousState(el),
            STATE      = getCurrentState(el, configs, PREV_STATE);
            
      triggerTargets(el, configs.targets || [configs], STATE, PREV_STATE, direction);
    }
  }
}

function triggerTargets(el, targetConfigs, state, prevState, direction){
  for(let configs of targetConfigs){
    let target = el.querySelector(configs.targetSelector || configs.triggerSelector);
    triggerTarget(el, target, configs.events, state, prevState, direction);
  }
}

function triggerTarget(el, target, events={}, state, prevState, direction){
  const { ENTERING, ENTERED, IN_VIEW, TRIGGERED, LEAVING, LEFT, progress } = state;

  if(IN_VIEW && events.onTick) events.onTick(target, direction, progress);
  else if(LEFT && events.onTick) events.onTick(target, direction, 1);
  else if(events.onTick) events.onTick(target, direction, 0);
  
  if(ENTERING && events.onEnter) events.onEnter(target, direction);
  if(LEAVING && events.onLeave) events.onLeave(target, direction);
  

  toggleClass(el, ENTERED_CLASS, ENTERED);
  toggleClass(el, LEFT_CLASS, LEFT);
  toggleClass(el, IN_VIEW_CLASS, IN_VIEW);
  toggleClass(el, TRIGGERED_CLASS, TRIGGERED && !prevState.TRIGGERED, false);
}

function toggleClass(target, className, toggleOn, toggleOff){
  if(toggleOn)
    target.classList.add(className);
  else if(toggleOff!==false)
    target.classList.remove(className);
}

function getPreviousState(el){
  return {
    ENTERED: el.classList.contains(ENTERED_CLASS),
    LEFT: el.classList.contains(LEFT_CLASS),
    IN_VIEW: el.classList.contains(IN_VIEW_CLASS),
    TRIGGERED: el.classList.contains(TRIGGERED_CLASS)
  };
}

function getCurrentState(el, configs, prevState){
  let rect = el.getBoundingClientRect();

  let { offset, topOffset, bottomOffset, triggerPointOffset } = getOffsets(el, configs);

  let entered  = rect.top + topOffset + triggerPointOffset <= 0,
      left     = -rect.top > el.offsetHeight + bottomOffset + triggerPointOffset,
      progress = 1-(rect.top+triggerPointOffset+el.offsetHeight+bottomOffset-0.5)/(el.offsetHeight - topOffset + bottomOffset);

  return {
    ENTERING: (entered && !left) && (!prevState.ENTERED || prevState.LEFT),
    ENTERED: entered,
    TRIGGERED: prevState.TRIGGERED || entered,
    IN_VIEW: entered && !left,
    LEAVING: (left || !entered) && prevState.IN_VIEW,
    LEFT: left,
    progress
  }
}

function getOffsets(el, configs){
  return {
    offset: getOffset(el, el.getAttribute('data-scroll-offset') || configs.offset || 0 ),
    topOffset: getOffset(el, el.getAttribute('data-scroll-top-offset') || configs.offset || configs.topOffset || 0),
    bottomOffset: getOffset(el, el.getAttribute('data-scroll-bottom-offset') || configs.offset || configs.bottomOffset || 0),
    triggerPointOffset: getTriggerPointOffset(el.getAttribute('data-scroll-trigger-point') || configs.triggerPoint || 'top')
  };
}

function getOffset(el, offset) {
  if(typeof(offset)==='number') return offset;
  if(!offset) return false;
  if(offset.indexOf('%')!==-1)
    return el.offsetHeight * offset.replace('%', '')/100;
  if(offset.indexOf('vh')!==-1){
    return getDocHeight() * offset.replace('vh', '')/100;
  }

  return +offset;
}

function getTriggerPointOffset(triggerPoint){
  switch(triggerPoint){
    case 'top':
      return 0;
    case 'bottom':
      return -getDocHeight();
    case 'middle':
      return -getDocheight()/2;
    default:
      return 0;
  }
}

function getDocHeight(){
  return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
}

export class ScrollTargets {
  constructor(){
    this.events = [];
    this.prevPosition = 0;
    this.direction = 'START';
    this.prevDirection = 'START';
    this.position = 0;
    this.animationFrame = 0;
    this.targets = [];
  }
  
  init = () => {
    // default scroll selector
    this.add({ triggerSelector: '.scroll-target' });
    // first tick;
    this.triggerScrollTargets();

    window.addEventListener('scroll', this.scroll, true);
    window.addEventListener('touchmove', this.scroll, true);
  }

  scroll = () => {
    window.requestAnimationFrame(this.triggerScrollTargets);
  }

  triggerScrollTargets = () => {
    this.position = window.scrollY || window.pageYOffset;

    if(this.position===this.prevPosition) this.direction = this.prevDirection;
    else this.direction = this.position < this.prevPosition ? 'REVERSE' : 'FORWARD';

    triggerScrollTargets(this.targets, this.direction);

    this.prevPosition = this.position;
    this.prevDirection = this.direction;
  }

  /**
    *
    * @typedef {Object} Target
    * @property {string} triggerSelector - (required) document.querySelectorAll compatable string. Elements that will trigger events,
    * @property {string} targetSelector - (default: triggerSelector) class selector for a single element. must be child of triggerSelector. the element to apply scroll classes / event callbacks to,
    * @property {Target[]} targets - add multiple target events, takes targetSelector and events properties,
    * @property {string} triggerPoint - (default: 'top', options: 'top', 'middle', 'bottom') part of viewPort event is triggered,
    * @property {(number|string)} offset - (default: 0) offset for both enter and leave events. essentially shifts entire frame by this valut. if defined as % (eg '50%'), offsets by % of element's outerHeight,
    * @property {(number|string)} topOffset - (default: 0) offset from triggerPoint for enter events. if defined as % (eg '50%'), offsets by % of element's outerHeight,
    * @property {(number|string)} bottomOffset - (default: 0) offset from triggerPoint for leave events. If defined as %, (eg '50%'), offsets by % of element's outerHeight,
    * @property {object} events - event callbacks
    * @property {eventCallback} events.onEnter - event for the exact moment when element hits the triggerPoint,
    * @property {eventCallback} events.onLeave - event for the exact moment when element (including outerHeight) goes outside the triggerPoint
    * @property {eventCallback} events.onTick - when element is in view, event fires for each scroll tick. Also gives % scroll progress param as decimal.
    *
    * @callback eventCallback
    * @param {HtmlElement} el - the triggered html target element
    * @param {string} direction - the direction scroll is moving ('FORWARD' or 'REVERSE')
    * @param {number} progress - for onTick callback. percent progress for selector
    *
  **/

  add = (target) => {
    let els = document.querySelectorAll(target.triggerSelector);
    this.targets = [...this.targets, {...target, els}];

    return this;
  }

  reload = () => {
    let targets = [];
    for(let t of this.targets)
      targets.push({...t, els: document.querySelectorAll(t.selector)});

    this.targets = targets;

    return this;
  }
  
  clear = () => {
    this.targets = [];
    this.direction = 'START';
    
    return this;
  }
}


const scrollEvents = new ScrollTargets();

export default scrollEvents

function getOffset(el, offset) {
  if(typeof(offset)==='number') return offset;
  if(!offset) return false;
  if(offset.indexOf('%')!==-1)
    return el.offsetHeight * offset.replace('%', '')/100;
  if(offset.indexOf('vh')!==-1){
    let docHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    return docHeight * offset.replace('vh', '')/100;
  }

  return +offset;
}

function getTriggerPointOffset(triggerPoint, docHeight){
  switch(triggerPoint){
    case 'top':
      return 0;
    case 'bottom':
      return -docHeight;
    case 'middle':
      return -docHeight/2;
    default:
      return 0;
  }
}



// WEBPACK FOOTER //
// ./src/lib/scroll-events.js